import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Download,
  Save,
  Calendar,
  Users,
  CheckCircle,
  Clock,
  User,
  AlertTriangle,
  Target,
  Activity,
  Eye,
  Printer,
  X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { planoTrabalhoService, AcaoDB } from '../services/planoTrabalhoService';

interface AtaDigital {
  id: string;
  id_empresa: string;
  id_usuario: string;
  numero_ata: number;
  tipo: 'ordinaria' | 'extraordinaria';
  data_reuniao: string;
  local_reuniao: string;
  horario_inicio: string;
  horario_fim: string;
  presidente: string;
  secretario: string;
  membros_presentes: string[];
  membros_ausentes: string[];
  convidados: string[];
  pauta: string[];
  deliberacoes: Deliberacao[];
  proxima_reuniao: string;
  assinaturas: Assinatura[];
  status: 'rascunho' | 'aprovada' | 'assinada';
  created_at: string;
  updated_at: string;
}

interface Deliberacao {
  id: string;
  descricao: string;
  responsavel: string;
  prazo: string;
  status: 'pendente' | 'em_andamento' | 'concluida';
}

interface Assinatura {
  id: string;
  nome: string;
  cargo: string;
  tipo: 'efetivo' | 'suplente' | 'presidente' | 'secretario';
  assinado: boolean;
  data_assinatura?: string;
  hash_assinatura?: string;
}

const AtaDigitalTab: React.FC = () => {
  const { user, empresaSelecionada } = useAuth();
  const [atas, setAtas] = useState<AtaDigital[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostrarModalAta, setMostrarModalAta] = useState(false);
  const [mostrarModalAssinatura, setMostrarModalAssinatura] = useState(false);
  const [ataParaAssinar, setAtaParaAssinar] = useState<AtaDigital | null>(null);
  const [ataEditando, setAtaEditando] = useState<AtaDigital | null>(null);
  const [atividadesPlanoTrabalho, setAtividadesPlanoTrabalho] = useState<AcaoDB[]>([]);
  const [sugestoesEditaveis, setSugestoesEditaveis] = useState<{[mes: number]: string[]}>({});
  const [mostrarGerenciarSugestoes, setMostrarGerenciarSugestoes] = useState(false);
  const [mesEditando, setMesEditando] = useState<number | null>(null);
  const [novaSugestao, setNovaSugestao] = useState('');
  const [carregandoSugestoes, setCarregandoSugestoes] = useState(false);
  const [mostrarModalDeliberacao, setMostrarModalDeliberacao] = useState(false);
  const [deliberacaoEditando, setDeliberacaoEditando] = useState<Deliberacao | null>(null);
  const [indiceDeliberacaoEditando, setIndiceDeliberacaoEditando] = useState<number | null>(null);
  const [novaAta, setNovaAta] = useState<Partial<AtaDigital>>({
    tipo: 'ordinaria',
    data_reuniao: new Date().toISOString().split('T')[0],
    local_reuniao: '',
    horario_inicio: '14:00',
    horario_fim: '16:00',
    presidente: '',
    secretario: '',
    membros_presentes: [],
    membros_ausentes: [],
    convidados: [],
    pauta: [],
    deliberacoes: [],
    proxima_reuniao: '',
    assinaturas: [],
    status: 'rascunho'
  });

  // Sugestões automáticas baseadas no mês
  const sugestoesMensais = {
    1: {
      pauta: [
        'Aprovação do Plano de Trabalho da CIPA',
        'Eleição da Mesa Diretora',
        'Definição do cronograma anual',
        'Análise do Mapa de Riscos',
        'Planejamento da SIPAT'
      ],
      deliberacoes: [
        'Aprovar Plano de Trabalho 2024',
        'Definir cronograma de inspeções mensais',
        'Planejar campanhas de segurança'
      ]
    },
    2: {
      pauta: [
        'Acompanhamento do Plano de Trabalho',
        'Resultados das inspeções de janeiro',
        'Treinamentos de segurança realizados',
        'Análise de acidentes e incidentes',
        'Preparação para SIPAT'
      ],
      deliberacoes: [
        'Ajustar cronograma de inspeções',
        'Implementar melhorias identificadas',
        'Organizar SIPAT'
      ]
    },
    3: {
      pauta: [
        'Avaliação do Plano de Trabalho',
        'Inspeções de segurança realizadas',
        'Treinamentos de EPIs',
        'Análise de indicadores de segurança',
        'Preparação da SIPAT'
      ],
      deliberacoes: [
        'Corrigir não conformidades',
        'Ampliar treinamentos',
        'Finalizar organização da SIPAT'
      ]
    },
    4: {
      pauta: [
        'Execução da SIPAT',
        'Avaliação dos resultados',
        'Inspeções de segurança',
        'Treinamentos realizados',
        'Análise de acidentes'
      ],
      deliberacoes: [
        'Avaliar resultados da SIPAT',
        'Implementar sugestões dos funcionários',
        'Ajustar cronograma anual'
      ]
    },
    5: {
      pauta: [
        'Acompanhamento pós-SIPAT',
        'Inspeções de segurança',
        'Treinamentos de combate a incêndio',
        'Análise de indicadores',
        'Preparação para Semana do Meio Ambiente'
      ],
      deliberacoes: [
        'Implementar melhorias pós-SIPAT',
        'Organizar treinamentos de emergência',
        'Planejar ações ambientais'
      ]
    },
    6: {
      pauta: [
        'Semana do Meio Ambiente',
        'Inspeções de segurança',
        'Treinamentos realizados',
        'Análise de acidentes',
        'Preparação para férias coletivas'
      ],
      deliberacoes: [
        'Avaliar ações ambientais',
        'Planejar cobertura durante férias',
        'Ajustar cronograma'
      ]
    },
    7: {
      pauta: [
        'Cobertura durante férias',
        'Inspeções de segurança',
        'Treinamentos de verão',
        'Análise de indicadores',
        'Preparação para Agosto Dourado'
      ],
      deliberacoes: [
        'Ajustar cronograma de férias',
        'Implementar medidas de verão',
        'Planejar campanha de aleitamento'
      ]
    },
    8: {
      pauta: [
        'Agosto Dourado - Aleitamento Materno',
        'Inspeções de segurança',
        'Treinamentos realizados',
        'Análise de acidentes',
        'Preparação para Setembro Amarelo'
      ],
      deliberacoes: [
        'Avaliar campanha de aleitamento',
        'Planejar ações de saúde mental',
        'Ajustar cronograma'
      ]
    },
    9: {
      pauta: [
        'Setembro Amarelo - Prevenção ao Suicídio',
        'Inspeções de segurança',
        'Treinamentos de saúde mental',
        'Análise de indicadores',
        'Preparação para Outubro Rosa'
      ],
      deliberacoes: [
        'Implementar ações de saúde mental',
        'Planejar campanha de prevenção ao câncer',
        'Ajustar cronograma'
      ]
    },
    10: {
      pauta: [
        'Outubro Rosa - Prevenção ao Câncer',
        'Inspeções de segurança',
        'Treinamentos realizados',
        'Análise de acidentes',
        'Preparação para Novembro Azul'
      ],
      deliberacoes: [
        'Avaliar campanha de prevenção',
        'Planejar ações de saúde masculina',
        'Ajustar cronograma'
      ]
    },
    11: {
      pauta: [
        'Novembro Azul - Saúde Masculina',
        'Inspeções de segurança',
        'Treinamentos realizados',
        'Análise de acidentes',
        'Preparação para balanço anual'
      ],
      deliberacoes: [
        'Avaliar campanha de saúde masculina',
        'Iniciar balanço anual',
        'Planejar eleições da CIPA'
      ]
    },
    12: {
      pauta: [
        'Balanço anual da CIPA',
        'Avaliação do Plano de Trabalho',
        'Resultados das ações realizadas',
        'Preparação para eleições',
        'Planejamento do próximo ano'
      ],
      deliberacoes: [
        'Aprovar balanço anual',
        'Planejar eleições da CIPA',
        'Definir metas para próximo ano'
      ]
    }
  };

  useEffect(() => {
    carregarAtas();
    carregarAtividadesPlanoTrabalho();
  }, [empresaSelecionada]);

  const carregarAtividadesPlanoTrabalho = async () => {
    if (!empresaSelecionada?.id || !user?.id) return;
    
    setCarregandoSugestoes(true);
    try {
      const planoCompleto = await planoTrabalhoService.obterPlanoCompleto(empresaSelecionada.id);
      if (planoCompleto?.acoes) {
        setAtividadesPlanoTrabalho(planoCompleto.acoes);
        gerarSugestoesComPlanoTrabalho(planoCompleto.acoes);
      }
    } catch (error) {
      console.error('Erro ao carregar atividades do plano de trabalho:', error);
    } finally {
      setCarregandoSugestoes(false);
    }
  };

  const gerarSugestoesComPlanoTrabalho = (acoes: AcaoDB[]) => {
    const sugestoesPorMes: {[mes: number]: string[]} = {};
    
    // Inicializar com sugestões padrão
    Object.keys(sugestoesMensais).forEach(mes => {
      const mesNum = parseInt(mes);
      sugestoesPorMes[mesNum] = [...sugestoesMensais[mesNum as keyof typeof sugestoesMensais].pauta];
    });

    // Adicionar atividades do plano de trabalho baseadas na data de início
    acoes.forEach(acao => {
      if (acao.data_inicio) {
        const dataInicio = new Date(acao.data_inicio);
        const mes = dataInicio.getMonth() + 1;
        
        if (sugestoesPorMes[mes]) {
          const sugestaoExistente = sugestoesPorMes[mes].find(item => 
            item.toLowerCase().includes(acao.descricao.toLowerCase().substring(0, 20))
          );
          
          if (!sugestaoExistente) {
            sugestoesPorMes[mes].push(`Acompanhamento: ${acao.descricao}`);
          }
        }
      }
    });

    setSugestoesEditaveis(sugestoesPorMes);
  };

  const adicionarSugestao = (mes: number) => {
    if (!novaSugestao.trim()) return;
    
    setSugestoesEditaveis(prev => ({
      ...prev,
      [mes]: [...(prev[mes] || []), novaSugestao.trim()]
    }));
    setNovaSugestao('');
  };

  const removerSugestao = (mes: number, index: number) => {
    setSugestoesEditaveis(prev => ({
      ...prev,
      [mes]: prev[mes]?.filter((_, i) => i !== index) || []
    }));
  };

  const editarSugestao = (mes: number, index: number, novoTexto: string) => {
    setSugestoesEditaveis(prev => ({
      ...prev,
      [mes]: prev[mes]?.map((item, i) => i === index ? novoTexto : item) || []
    }));
  };

  const editarDeliberacao = (index: number) => {
    const deliberacao = novaAta.deliberacoes?.[index];
    if (deliberacao) {
      setDeliberacaoEditando(deliberacao);
      setIndiceDeliberacaoEditando(index);
      setMostrarModalDeliberacao(true);
    }
  };

  const salvarDeliberacao = () => {
    if (!deliberacaoEditando || indiceDeliberacaoEditando === null) return;

    const novasDeliberacoes = [...(novaAta.deliberacoes || [])];
    novasDeliberacoes[indiceDeliberacaoEditando] = deliberacaoEditando;
    
    setNovaAta({...novaAta, deliberacoes: novasDeliberacoes});
    setMostrarModalDeliberacao(false);
    setDeliberacaoEditando(null);
    setIndiceDeliberacaoEditando(null);
  };

  const carregarAtas = async () => {
    if (!empresaSelecionada?.id || !user?.id) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Por enquanto, usar dados mockados
      const atasMock: AtaDigital[] = [
        {
          id: '1',
          id_empresa: empresaSelecionada.id,
          id_usuario: user.id,
          numero_ata: 1,
          tipo: 'ordinaria',
          data_reuniao: '2024-01-15',
          local_reuniao: 'Sala de Reuniões',
          horario_inicio: '14:00',
          horario_fim: '16:00',
          presidente: 'João Silva',
          secretario: 'Maria Santos',
          membros_presentes: ['João Silva', 'Maria Santos', 'Pedro Costa'],
          membros_ausentes: ['Ana Lima'],
          convidados: ['Dr. Carlos - SESMT'],
          pauta: ['Aprovação do Plano de Trabalho', 'Eleição da Mesa Diretora'],
          deliberacoes: [
            { id: '1', descricao: 'Aprovar Plano de Trabalho 2024', responsavel: 'CIPA', prazo: '30 dias', status: 'concluida' }
          ],
          proxima_reuniao: '2024-02-15',
          assinaturas: [
            { id: '1', nome: 'João Silva', cargo: 'Presidente', tipo: 'presidente', assinado: true, data_assinatura: '2024-01-15' }
          ],
          status: 'assinada',
          created_at: '2024-01-15T14:00:00Z',
          updated_at: '2024-01-15T16:00:00Z'
        }
      ];
      
      setAtas(atasMock);
    } catch (error) {
      console.error('Erro ao carregar atas:', error);
    } finally {
      setLoading(false);
    }
  };

  const gerarSugestoesAutomaticas = () => {
    // Usar o mês da data da reunião da ata, ou o mês atual se não houver data
    const dataReuniao = novaAta.data_reuniao ? new Date(novaAta.data_reuniao) : new Date();
    const mesAta = dataReuniao.getMonth() + 1;
    
    // Usar sugestões editáveis se disponíveis, senão usar padrão
    const sugestoesPauta = sugestoesEditaveis[mesAta] || sugestoesMensais[mesAta as keyof typeof sugestoesMensais]?.pauta || [];
    const sugestoesDeliberacoes = sugestoesMensais[mesAta as keyof typeof sugestoesMensais]?.deliberacoes || [];
    
    // Buscar atividades do plano de trabalho para o mês atual
    const atividadesDoMes = atividadesPlanoTrabalho.filter(acao => {
      if (!acao.data_inicio) return false;
      const dataInicio = new Date(acao.data_inicio);
      return dataInicio.getMonth() + 1 === mesAta;
    });

    // Criar deliberações combinando sugestões padrão e atividades do plano
    const deliberacoesCombinadas = [
      // Deliberações padrão do mês
      ...sugestoesDeliberacoes.map((del, index) => ({
        id: `del-padrao-${index}`,
        descricao: del,
        responsavel: 'CIPA',
        prazo: '30 dias',
        status: 'pendente' as const
      })),
      // Atividades do plano de trabalho
      ...atividadesDoMes.map((acao, index) => ({
        id: `del-plano-${acao.id}`,
        descricao: acao.descricao,
        responsavel: acao.responsavel || 'CIPA',
        prazo: acao.prazo || '30 dias',
        status: acao.status === 'concluida' ? 'concluida' as const : 'pendente' as const
      }))
    ];
    
    setNovaAta(prev => ({
      ...prev,
      pauta: sugestoesPauta,
      deliberacoes: deliberacoesCombinadas
    }));
  };

  const salvarAta = async () => {
    if (!novaAta.data_reuniao || !novaAta.local_reuniao) {
      alert('Preencha os campos obrigatórios');
      return;
    }

    try {
      // Limpar linhas vazias da pauta
      const pautaLimpa = novaAta.pauta?.filter(item => item.trim() !== '') || [];
      
      const ataCompleta: AtaDigital = {
        id: ataEditando?.id || `ata-${Date.now()}`,
        id_empresa: empresaSelecionada?.id || '',
        id_usuario: user?.id || '',
        numero_ata: ataEditando?.numero_ata || atas.length + 1,
        ...novaAta,
        pauta: pautaLimpa,
        created_at: ataEditando?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as AtaDigital;

      if (ataEditando) {
        setAtas(prev => prev.map(a => a.id === ataCompleta.id ? ataCompleta : a));
      } else {
        setAtas(prev => [...prev, ataCompleta]);
      }

      setMostrarModalAta(false);
      setAtaEditando(null);
      setNovaAta({
        tipo: 'ordinaria',
        data_reuniao: new Date().toISOString().split('T')[0],
        local_reuniao: '',
        horario_inicio: '14:00',
        horario_fim: '16:00',
        presidente: '',
        secretario: '',
        membros_presentes: [],
        membros_ausentes: [],
        convidados: [],
        pauta: [],
        deliberacoes: [],
        proxima_reuniao: '',
        assinaturas: [],
        status: 'rascunho'
      });

      console.log('Ata salva:', ataCompleta);
    } catch (error) {
      console.error('Erro ao salvar ata:', error);
      alert('Erro ao salvar ata. Tente novamente.');
    }
  };

  const excluirAta = (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta ata?')) {
      setAtas(prev => prev.filter(a => a.id !== id));
    }
  };

  const editarAta = (ata: AtaDigital) => {
    setAtaEditando(ata);
    setNovaAta(ata);
    setMostrarModalAta(true);
  };

  const abrirModalAssinatura = (ata: AtaDigital) => {
    setAtaParaAssinar(ata);
    setMostrarModalAssinatura(true);
  };

  const assinarAta = (nome: string, cargo: string) => {
    if (!ataParaAssinar) return;

    const novaAssinatura: Assinatura = {
      id: `ass-${Date.now()}`,
      nome: nome,
      cargo: cargo,
      tipo: cargo.toLowerCase().includes('presidente') ? 'presidente' : 
            cargo.toLowerCase().includes('secretário') || cargo.toLowerCase().includes('secretario') ? 'secretario' : 'efetivo',
      assinado: true,
      data_assinatura: new Date().toISOString(),
      hash_assinatura: `hash-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };

    const ataAtualizada = {
      ...ataParaAssinar,
      assinaturas: [...ataParaAssinar.assinaturas, novaAssinatura],
      status: 'assinada' as const,
      updated_at: new Date().toISOString()
    };

    setAtas(prev => prev.map(a => a.id === ataAtualizada.id ? ataAtualizada : a));
    setMostrarModalAssinatura(false);
    setAtaParaAssinar(null);

    console.log('Ata assinada:', ataAtualizada);
  };

  const imprimirAta = (ata: AtaDigital) => {
    const conteudoImpressao = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Ata CIPA - ${ata.numero_ata}</title>
    <style>
        @page {
            size: A4;
            margin: 2cm;
        }
        
        body {
            font-family: 'Times New Roman', serif;
            font-size: 12pt;
            line-height: 1.4;
            color: #000;
            margin: 0;
            padding: 0;
        }
        
        .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
        }
        
        .header h1 {
            font-size: 16pt;
            font-weight: bold;
            margin: 0 0 10px 0;
            text-transform: uppercase;
        }
        
        .header h2 {
            font-size: 14pt;
            font-weight: bold;
            margin: 0 0 5px 0;
        }
        
        .header p {
            font-size: 11pt;
            margin: 2px 0;
        }
        
        .info-box {
            background-color: #f5f5f5;
            border: 1px solid #ccc;
            padding: 15px;
            margin: 20px 0;
            border-radius: 5px;
        }
        
        .info-row {
            display: flex;
            margin-bottom: 8px;
        }
        
        .info-label {
            font-weight: bold;
            min-width: 120px;
        }
        
        .section {
            margin: 25px 0;
        }
        
        .section h3 {
            font-size: 13pt;
            font-weight: bold;
            margin-bottom: 10px;
            text-transform: uppercase;
            border-bottom: 1px solid #000;
            padding-bottom: 5px;
        }
        
        .lista {
            margin: 10px 0;
        }
        
        .lista li {
            margin-bottom: 5px;
            padding-left: 5px;
        }
        
        .deliberacao {
            margin: 10px 0;
            padding: 10px;
            border-left: 3px solid #333;
            background-color: #f9f9f9;
        }
        
        .assinaturas {
            margin-top: 40px;
            page-break-inside: avoid;
        }
        
        .assinatura-box {
            display: inline-block;
            width: 200px;
            margin: 20px 20px 20px 0;
            text-align: center;
            border-bottom: 1px solid #000;
            padding-bottom: 5px;
        }
        
        .assinatura-nome {
            font-weight: bold;
            margin-top: 30px;
        }
        
        .assinatura-cargo {
            font-size: 10pt;
            margin-top: 5px;
        }
        
        .footer {
            margin-top: 50px;
            text-align: center;
            font-size: 10pt;
            color: #666;
            border-top: 1px solid #ccc;
            padding-top: 15px;
        }
        
        .hash {
            font-family: monospace;
            font-size: 9pt;
            background-color: #f0f0f0;
            padding: 5px;
            border-radius: 3px;
            word-break: break-all;
        }
        
        @media print {
            body { -webkit-print-color-adjust: exact; }
            .no-print { display: none; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>ATA DE REUNIÃO DA CIPA</h1>
        <h2>${empresaSelecionada?.nome || 'Empresa'}</h2>
        <p>CNPJ: ${empresaSelecionada?.cnpj || 'N/A'}</p>
        <p>Endereço: N/A</p>
    </div>

    <div class="info-box">
        <div class="info-row">
            <span class="info-label">Número da Ata:</span>
            <span>${ata.numero_ata}</span>
        </div>
        <div class="info-row">
            <span class="info-label">Tipo:</span>
            <span>${ata.tipo === 'ordinaria' ? 'Ordinária' : 'Extraordinária'}</span>
        </div>
        <div class="info-row">
            <span class="info-label">Data:</span>
            <span>${new Date(ata.data_reuniao).toLocaleDateString('pt-BR')}</span>
        </div>
        <div class="info-row">
            <span class="info-label">Local:</span>
            <span>${ata.local_reuniao}</span>
        </div>
        <div class="info-row">
            <span class="info-label">Horário:</span>
            <span>${ata.horario_inicio} às ${ata.horario_fim}</span>
        </div>
    </div>

    <div class="section">
        <h3>Mesa Diretora</h3>
        <div class="info-row">
            <span class="info-label">Presidente:</span>
            <span>${ata.presidente || 'Não definido'}</span>
        </div>
        <div class="info-row">
            <span class="info-label">Secretário:</span>
            <span>${ata.secretario || 'Não definido'}</span>
        </div>
    </div>

    <div class="section">
        <h3>Lista de Presença</h3>
        <div style="display: flex; gap: 30px;">
            <div style="flex: 1;">
                <h4>Membros Presentes:</h4>
                <ul class="lista">
                    ${ata.membros_presentes.map(membro => `<li>${membro}</li>`).join('')}
                </ul>
            </div>
            <div style="flex: 1;">
                <h4>Membros Ausentes:</h4>
                <ul class="lista">
                    ${ata.membros_ausentes.map(membro => `<li>${membro}</li>`).join('')}
                </ul>
            </div>
        </div>
        ${ata.convidados.length > 0 ? `
        <div>
            <h4>Convidados:</h4>
            <ul class="lista">
                ${ata.convidados.map(convidado => `<li>${convidado}</li>`).join('')}
            </ul>
        </div>
        ` : ''}
    </div>

    <div class="section">
        <h3>Pauta da Reunião</h3>
        <ol class="lista">
            ${ata.pauta.map(item => `<li>${item}</li>`).join('')}
        </ol>
    </div>

    <div class="section">
        <h3>Deliberações</h3>
        ${ata.deliberacoes.map((del, index) => `
        <div class="deliberacao">
            <strong>${index + 1}. ${del.descricao}</strong><br>
            <span>Responsável: ${del.responsavel}</span><br>
            <span>Prazo: ${del.prazo}</span><br>
            <span>Status: ${del.status}</span>
        </div>
        `).join('')}
    </div>

    ${ata.proxima_reuniao ? `
    <div class="section">
        <h3>Próxima Reunião</h3>
        <p>Data: ${new Date(ata.proxima_reuniao).toLocaleDateString('pt-BR')}</p>
    </div>
    ` : ''}

    <div class="section assinaturas">
        <h3>Assinaturas</h3>
        ${ata.assinaturas.length > 0 ? `
        <div style="display: flex; flex-wrap: wrap; gap: 20px;">
            ${ata.assinaturas.map(ass => `
            <div class="assinatura-box">
                <div class="assinatura-nome">${ass.nome}</div>
                <div class="assinatura-cargo">${ass.cargo}</div>
                <div style="margin-top: 10px; font-size: 9pt;">
                    ${ass.assinado ? `Assinado em ${new Date(ass.data_assinatura || '').toLocaleDateString('pt-BR')}` : 'Pendente'}
                </div>
            </div>
            `).join('')}
        </div>
        ` : '<p>Nenhuma assinatura registrada</p>'}
    </div>

    ${ata.assinaturas.some(ass => ass.hash_assinatura) ? `
    <div class="section">
        <h3>Hash de Segurança</h3>
        <div class="hash">
            ${ata.assinaturas.map(ass => ass.hash_assinatura).filter(Boolean).join(', ')}
        </div>
    </div>
    ` : ''}

    <div class="footer">
        <p>Documento gerado em ${new Date().toLocaleString('pt-BR')}</p>
        <p>Sistema CIPA Fácil - Conforme NR-05</p>
    </div>
</body>
</html>
    `;

    const janelaImpressao = window.open('', '_blank');
    if (janelaImpressao) {
      janelaImpressao.document.write(conteudoImpressao);
      janelaImpressao.document.close();
      
      // Aguardar o conteúdo carregar e então imprimir
      janelaImpressao.onload = () => {
        setTimeout(() => {
          janelaImpressao.print();
        }, 500);
      };
    }
  };

  const exportarAta = (ata: AtaDigital) => {
    const conteudo = `
ATA DE REUNIÃO DA CIPA - ${empresaSelecionada?.nome || 'Empresa'}
Número: ${ata.numero_ata} | Tipo: ${ata.tipo === 'ordinaria' ? 'Ordinária' : 'Extraordinária'}
Data: ${new Date(ata.data_reuniao).toLocaleDateString('pt-BR')}
Local: ${ata.local_reuniao}
Horário: ${ata.horario_inicio} às ${ata.horario_fim}

PRESIDENTE: ${ata.presidente}
SECRETÁRIO: ${ata.secretario}

MEMBROS PRESENTES:
${ata.membros_presentes.map(membro => `• ${membro}`).join('\n')}

MEMBROS AUSENTES:
${ata.membros_ausentes.map(membro => `• ${membro}`).join('\n')}

CONVIDADOS:
${ata.convidados.map(convidado => `• ${convidado}`).join('\n')}

PAUTA:
${ata.pauta.map((item, index) => `${index + 1}. ${item}`).join('\n')}

DELIBERAÇÕES:
${ata.deliberacoes.map((del, index) => `${index + 1}. ${del.descricao} - Responsável: ${del.responsavel} - Prazo: ${del.prazo}`).join('\n')}

PRÓXIMA REUNIÃO: ${ata.proxima_reuniao ? new Date(ata.proxima_reuniao).toLocaleDateString('pt-BR') : 'A definir'}

ASSINATURAS:
${ata.assinaturas.length > 0 ? 
  ata.assinaturas.map(ass => `• ${ass.nome} (${ass.cargo}) - ${ass.assinado ? `Assinado em ${new Date(ass.data_assinatura || '').toLocaleDateString('pt-BR')}` : 'Pendente'}`).join('\n') :
  'Nenhuma assinatura registrada'
}

HASH DE SEGURANÇA: ${ata.assinaturas.map(ass => ass.hash_assinatura).filter(Boolean).join(', ') || 'N/A'}
    `;

    const blob = new Blob([conteudo], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ata-cipa-${ata.numero_ata}-${new Date(ata.data_reuniao).getFullYear()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Activity className="w-8 h-8 text-blue-600 animate-pulse mx-auto mb-2" />
          <p className="text-gray-600">Carregando atas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Ata Digital da CIPA</h1>
            <p className="text-green-100">
              {empresaSelecionada?.nome} - Ano {new Date().getFullYear()}
            </p>
            <p className="text-sm text-green-200 mt-1">
              Conforme NR-05 - Art. 5.17 (Reuniões Ordinárias Mensais)
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setMostrarGerenciarSugestoes(true);
                carregarAtividadesPlanoTrabalho(); // Refresh das atividades
              }}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Target className="w-4 h-4" />
              Gerenciar Sugestões
            </button>
            <button
              onClick={() => setMostrarModalAta(true)}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nova Ata
            </button>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-green-500">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-gray-700">Total de Atas</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{atas.length}</p>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Atas Assinadas</span>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {atas.filter(a => a.status === 'assinada').length}
          </p>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-yellow-500">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-gray-700">Rascunhos</span>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {atas.filter(a => a.status === 'rascunho').length}
          </p>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-purple-500">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">Este Mês</span>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {atas.filter(a => {
              const dataAta = new Date(a.data_reuniao);
              const agora = new Date();
              return dataAta.getMonth() === agora.getMonth() && 
                     dataAta.getFullYear() === agora.getFullYear();
            }).length}
          </p>
        </div>
      </div>

      {/* Cards dos Meses */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-green-600" />
            Atas Mensais da CIPA - {new Date().getFullYear()}
          </h3>
          <button
            onClick={() => setMostrarModalAta(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nova Ata
          </button>
        </div>

        {/* Grid de 12 meses - 3 linhas com 4 cards cada */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 12 }, (_, index) => {
            const mes = index + 1;
            const nomeMes = new Date(2024, index, 1).toLocaleDateString('pt-BR', { month: 'long' });
            const nomeMesCapitalizado = nomeMes.charAt(0).toUpperCase() + nomeMes.slice(1);
            
            // Buscar ata do mês
            const ataDoMes = atas.find(ata => {
              const dataAta = new Date(ata.data_reuniao);
              return dataAta.getMonth() + 1 === mes && dataAta.getFullYear() === new Date().getFullYear();
            });

            const coresPorMes = {
              1: 'from-blue-500 to-blue-600', // Janeiro
              2: 'from-pink-500 to-pink-600', // Fevereiro
              3: 'from-green-500 to-green-600', // Março
              4: 'from-yellow-500 to-yellow-600', // Abril
              5: 'from-purple-500 to-purple-600', // Maio
              6: 'from-red-500 to-red-600', // Junho
              7: 'from-orange-500 to-orange-600', // Julho
              8: 'from-teal-500 to-teal-600', // Agosto
              9: 'from-indigo-500 to-indigo-600', // Setembro
              10: 'from-rose-500 to-rose-600', // Outubro
              11: 'from-cyan-500 to-cyan-600', // Novembro
              12: 'from-emerald-500 to-emerald-600' // Dezembro
            };

            return (
              <div
                key={mes}
                className={`bg-gradient-to-br ${coresPorMes[mes as keyof typeof coresPorMes]} rounded-lg p-4 text-white cursor-pointer hover:shadow-lg transition-all duration-200 transform hover:scale-105`}
                onClick={() => {
                  if (ataDoMes) {
                    editarAta(ataDoMes);
                  } else {
                    // Criar nova ata para o mês
                    const dataReuniao = new Date(new Date().getFullYear(), index, 15).toISOString().split('T')[0];
                    setNovaAta({
                      tipo: 'ordinaria',
                      data_reuniao: dataReuniao,
                      local_reuniao: 'Sala de Reuniões',
                      horario_inicio: '14:00',
                      horario_fim: '16:00',
                      presidente: '',
                      secretario: '',
                      membros_presentes: [],
                      membros_ausentes: [],
                      convidados: [],
                      pauta: [],
                      deliberacoes: [],
                      proxima_reuniao: '',
                      assinaturas: [],
                      status: 'rascunho'
                    });
                    setMostrarModalAta(true);
                  }
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-2xl font-bold">{mes.toString().padStart(2, '0')}</div>
                  <div className="text-right">
                    {ataDoMes ? (
                      <div className="flex items-center gap-1">
                        {ataDoMes.status === 'assinada' ? (
                          <CheckCircle className="w-5 h-5 text-green-200" />
                        ) : ataDoMes.status === 'aprovada' ? (
                          <Clock className="w-5 h-5 text-yellow-200" />
                        ) : (
                          <Edit className="w-5 h-5 text-blue-200" />
                        )}
                      </div>
                    ) : (
                      <Plus className="w-5 h-5 text-white/70" />
                    )}
                  </div>
                </div>
                
                <div className="text-sm font-medium mb-2">{nomeMesCapitalizado}</div>
                
                {ataDoMes ? (
                  <div className="space-y-1 text-xs text-white/90">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span>{new Date(ataDoMes.data_reuniao).toLocaleDateString('pt-BR')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span>{ataDoMes.presidente || 'Não definido'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        ataDoMes.status === 'assinada' ? 'bg-green-200 text-green-800' :
                        ataDoMes.status === 'aprovada' ? 'bg-yellow-200 text-yellow-800' :
                        'bg-blue-200 text-blue-800'
                      }`}>
                        {ataDoMes.status === 'assinada' ? 'Assinada' :
                         ataDoMes.status === 'aprovada' ? 'Aprovada' : 'Rascunho'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-white/70">
                    <div className="flex items-center gap-1 mb-1">
                      <AlertTriangle className="w-3 h-3" />
                      <span>Sem ata</span>
                    </div>
                    <div className="text-white/50">Clique para criar</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Lista de atas existentes (se houver) */}
        {atas.length > 0 && (
          <div className="mt-8">
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-600" />
              Todas as Atas ({atas.length})
            </h4>
            <div className="space-y-3">
              {atas.map((ata) => (
                <div key={ata.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-medium text-gray-900">
                        Ata #{ata.numero_ata} - {new Date(ata.data_reuniao).toLocaleDateString('pt-BR')}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        ata.status === 'assinada' ? 'bg-green-100 text-green-800' :
                        ata.status === 'aprovada' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {ata.status === 'assinada' ? 'Assinada' :
                         ata.status === 'aprovada' ? 'Aprovada' : 'Rascunho'}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          imprimirAta(ata);
                        }}
                        className="text-gray-600 hover:text-gray-800 p-1"
                        title="Imprimir"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          exportarAta(ata);
                        }}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Exportar"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {ata.status !== 'assinada' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirModalAssinatura(ata);
                          }}
                          className="text-purple-600 hover:text-purple-800 p-1"
                          title="Assinar"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          editarAta(ata);
                        }}
                        className="text-green-600 hover:text-green-800 p-1"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          excluirAta(ata.id);
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal para Nova/Editar Ata */}
      {mostrarModalAta && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {ataEditando ? 'Editar Ata' : 'Nova Ata'}
            </h3>
            
            <div className="space-y-4">
              {/* Informações Básicas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Reunião *
                  </label>
                  <select
                    value={novaAta.tipo || 'ordinaria'}
                    onChange={(e) => setNovaAta({...novaAta, tipo: e.target.value as 'ordinaria' | 'extraordinaria'})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="ordinaria">Ordinária</option>
                    <option value="extraordinaria">Extraordinária</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data da Reunião *
                  </label>
                  <input
                    type="date"
                    value={novaAta.data_reuniao || ''}
                    onChange={(e) => setNovaAta({...novaAta, data_reuniao: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Local da Reunião *
                  </label>
                  <input
                    type="text"
                    value={novaAta.local_reuniao || ''}
                    onChange={(e) => setNovaAta({...novaAta, local_reuniao: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ex: Sala de Reuniões"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="time"
                      value={novaAta.horario_inicio || '14:00'}
                      onChange={(e) => setNovaAta({...novaAta, horario_inicio: e.target.value})}
                      className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <input
                      type="time"
                      value={novaAta.horario_fim || '16:00'}
                      onChange={(e) => setNovaAta({...novaAta, horario_fim: e.target.value})}
                      className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Botão para gerar sugestões automáticas */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-blue-900">Sugestões Automáticas</h4>
                    <p className="text-sm text-blue-700">
                      Gerar pauta e deliberações baseadas no mês atual e atividades do Plano de Trabalho
                    </p>
                    {atividadesPlanoTrabalho.length > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        📋 {atividadesPlanoTrabalho.length} atividades do plano de trabalho integradas
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setMostrarGerenciarSugestoes(true);
                        carregarAtividadesPlanoTrabalho(); // Refresh das atividades
                      }}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
                    >
                      <Edit className="w-4 h-4" />
                      Editar
                    </button>
                    <button
                      onClick={gerarSugestoesAutomaticas}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                      <Target className="w-4 h-4" />
                      Gerar Sugestões
                    </button>
                  </div>
                </div>
              </div>

              {/* Mesa Diretora */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Presidente
                  </label>
                  <input
                    type="text"
                    value={novaAta.presidente || ''}
                    onChange={(e) => setNovaAta({...novaAta, presidente: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Nome do presidente"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secretário
                  </label>
                  <input
                    type="text"
                    value={novaAta.secretario || ''}
                    onChange={(e) => setNovaAta({...novaAta, secretario: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Nome do secretário"
                  />
                </div>
              </div>

              {/* Pauta */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pauta da Reunião
                </label>
                <textarea
                  value={novaAta.pauta?.join('\n') || ''}
                  onChange={(e) => {
                    const linhas = e.target.value.split('\n');
                    setNovaAta({...novaAta, pauta: linhas});
                  }}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  rows={6}
                  placeholder="Digite cada item da pauta em uma linha separada. Use Enter para quebrar linha."
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Dica: Use Enter para criar novos itens da pauta
                </p>
              </div>

              {/* Deliberações */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deliberações da Reunião
                </label>
                <div className="space-y-3">
                  {novaAta.deliberacoes && novaAta.deliberacoes.length > 0 ? (
                    novaAta.deliberacoes.map((deliberacao, index) => (
                      <div key={deliberacao.id || index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            {index + 1}. {deliberacao.descricao}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => editarDeliberacao(index)}
                              className="text-blue-500 hover:text-blue-700 p-1"
                              title="Editar deliberação"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                const novasDeliberacoes = novaAta.deliberacoes?.filter((_, i) => i !== index) || [];
                                setNovaAta({...novaAta, deliberacoes: novasDeliberacoes});
                              }}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Remover deliberação"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-600">
                          <div>
                            <span className="font-medium">Responsável:</span> {deliberacao.responsavel}
                          </div>
                          <div>
                            <span className="font-medium">Prazo:</span> {deliberacao.prazo}
                          </div>
                          <div>
                            <span className="font-medium">Status:</span> 
                            <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                              deliberacao.status === 'concluida' ? 'bg-green-100 text-green-800' :
                              deliberacao.status === 'em_andamento' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {deliberacao.status === 'concluida' ? 'Concluída' :
                               deliberacao.status === 'em_andamento' ? 'Em Andamento' : 'Pendente'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Target className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p>Nenhuma deliberação adicionada</p>
                      <p className="text-sm">Use "Gerar Sugestões" para incluir atividades do plano de trabalho</p>
                    </div>
                  )}
                </div>
                
                <div className="mt-3">
                  <button
                    onClick={() => {
                      const novaDeliberacao = {
                        id: `del-manual-${Date.now()}`,
                        descricao: '',
                        responsavel: 'CIPA',
                        prazo: '30 dias',
                        status: 'pendente' as const
                      };
                      setNovaAta({
                        ...novaAta,
                        deliberacoes: [...(novaAta.deliberacoes || []), novaDeliberacao]
                      });
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar Deliberação Manual
                  </button>
                </div>
              </div>

              {/* Próxima Reunião */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Próxima Reunião
                </label>
                <input
                  type="date"
                  value={novaAta.proxima_reuniao || ''}
                  onChange={(e) => setNovaAta({...novaAta, proxima_reuniao: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setMostrarModalAta(false);
                  setAtaEditando(null);
                  setNovaAta({
                    tipo: 'ordinaria',
                    data_reuniao: new Date().toISOString().split('T')[0],
                    local_reuniao: '',
                    horario_inicio: '14:00',
                    horario_fim: '16:00',
                    presidente: '',
                    secretario: '',
                    membros_presentes: [],
                    membros_ausentes: [],
                    convidados: [],
                    pauta: [],
                    deliberacoes: [],
                    proxima_reuniao: '',
                    assinaturas: [],
                    status: 'rascunho'
                  });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={salvarAta}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {ataEditando ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Gerenciar Sugestões Mensais */}
      {mostrarGerenciarSugestoes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                Gerenciar Sugestões Mensais da Ata
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={carregarAtividadesPlanoTrabalho}
                  disabled={carregandoSugestoes}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
                  title="Atualizar sugestões com atividades do plano de trabalho"
                >
                  <Activity className={`w-4 h-4 ${carregandoSugestoes ? 'animate-pulse' : ''}`} />
                  {carregandoSugestoes ? 'Atualizando...' : 'Atualizar'}
                </button>
                <button
                  onClick={() => setMostrarGerenciarSugestoes(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>💡 Dica:</strong> As sugestões são geradas automaticamente baseadas nas atividades do Plano de Trabalho da CIPA. 
                Você pode adicionar, editar ou remover itens conforme necessário.
              </p>
              {carregandoSugestoes && (
                <div className="flex items-center gap-2 mt-2 text-blue-700">
                  <Activity className="w-4 h-4 animate-pulse" />
                  <span className="text-sm">Atualizando sugestões com atividades do plano de trabalho...</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 12 }, (_, index) => {
                const mes = index + 1;
                const nomeMes = new Date(2024, index, 1).toLocaleDateString('pt-BR', { month: 'long' });
                const nomeMesCapitalizado = nomeMes.charAt(0).toUpperCase() + nomeMes.slice(1);
                const sugestoesDoMes = sugestoesEditaveis[mes] || [];

                return (
                  <div key={mes} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">{nomeMesCapitalizado}</h4>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {sugestoesDoMes.length} itens
                      </span>
                    </div>

                    <div className="space-y-2 mb-3">
                      {sugestoesDoMes.map((sugestao, index) => (
                        <div key={index} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                          <input
                            type="text"
                            value={sugestao}
                            onChange={(e) => editarSugestao(mes, index, e.target.value)}
                            className="flex-1 text-sm border-none bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-1"
                          />
                          <button
                            onClick={() => removerSugestao(mes, index)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Remover"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={mesEditando === mes ? novaSugestao : ''}
                        onChange={(e) => {
                          setNovaSugestao(e.target.value);
                          setMesEditando(mes);
                        }}
                        placeholder="Nova sugestão..."
                        className="flex-1 text-sm p-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            adicionarSugestao(mes);
                          }
                        }}
                      />
                      <button
                        onClick={() => adicionarSugestao(mes)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm flex items-center gap-1"
                        title="Adicionar"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setMostrarGerenciarSugestoes(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Editar Deliberação */}
      {mostrarModalDeliberacao && deliberacaoEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Editar Deliberação
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <textarea
                  value={deliberacaoEditando.descricao}
                  onChange={(e) => setDeliberacaoEditando({
                    ...deliberacaoEditando,
                    descricao: e.target.value
                  })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Descreva a deliberação..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Responsável
                  </label>
                  <input
                    type="text"
                    value={deliberacaoEditando.responsavel}
                    onChange={(e) => setDeliberacaoEditando({
                      ...deliberacaoEditando,
                      responsavel: e.target.value
                    })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: CIPA, SESMT, RH..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prazo
                  </label>
                  <input
                    type="text"
                    value={deliberacaoEditando.prazo}
                    onChange={(e) => setDeliberacaoEditando({
                      ...deliberacaoEditando,
                      prazo: e.target.value
                    })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 30 dias, 15 dias..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={deliberacaoEditando.status}
                  onChange={(e) => setDeliberacaoEditando({
                    ...deliberacaoEditando,
                    status: e.target.value as 'pendente' | 'em_andamento' | 'concluida'
                  })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="pendente">Pendente</option>
                  <option value="em_andamento">Em Andamento</option>
                  <option value="concluida">Concluída</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setMostrarModalDeliberacao(false);
                  setDeliberacaoEditando(null);
                  setIndiceDeliberacaoEditando(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={salvarDeliberacao}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Assinatura */}
      {mostrarModalAssinatura && ataParaAssinar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              Assinar Ata #{ataParaAssinar.numero_ata}
            </h3>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Data:</strong> {new Date(ataParaAssinar.data_reuniao).toLocaleDateString('pt-BR')}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Local:</strong> {ataParaAssinar.local_reuniao}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seu Nome *
                </label>
                <input
                  type="text"
                  id="nomeAssinatura"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Digite seu nome completo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seu Cargo *
                </label>
                <select
                  id="cargoAssinatura"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Selecione seu cargo</option>
                  <option value="Presidente da CIPA">Presidente da CIPA</option>
                  <option value="Secretário da CIPA">Secretário da CIPA</option>
                  <option value="Membro Efetivo">Membro Efetivo</option>
                  <option value="Membro Suplente">Membro Suplente</option>
                  <option value="Representante dos Empregados">Representante dos Empregados</option>
                  <option value="Representante do Empregador">Representante do Empregador</option>
                </select>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Importante:</p>
                    <p>Ao assinar, você confirma que participou da reunião e concorda com o conteúdo da ata.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setMostrarModalAssinatura(false);
                  setAtaParaAssinar(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  const nome = (document.getElementById('nomeAssinatura') as HTMLInputElement)?.value;
                  const cargo = (document.getElementById('cargoAssinatura') as HTMLSelectElement)?.value;
                  
                  if (!nome || !cargo) {
                    alert('Preencha todos os campos obrigatórios');
                    return;
                  }
                  
                  assinarAta(nome, cargo);
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Assinar Ata
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AtaDigitalTab;
